import React, { useEffect, useState, Suspense, lazy, Fragment, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import store from '@src/store'
import { createBrowserRouter, RouterProvider, BrowserRouter, Route, Routes, useNavigate, useLocation, useRouteError, useLoaderData } from 'react-router'
import { Provider, useSelector, useDispatch } from 'react-redux'
import { closeSidebar, setCookiesAccepted, setScrollPosition, toggleSidebarLocked, closePricing, toggleSidebar } from '@src/mainSlice'
import { useGetCurrentQuery } from '@src/pages/stores/storesApi'
import { logout, getUserThunk } from '@pages/login/authSlice'
import { mainApiUrl, storeId } from '@constants'
import { setSearch, setAlbum } from '@components/filters/filtersSlice'
import { I18n, t, getLocale } from 'react-i18nify'
import { initBreakpoints, useBreakpoints } from 'react-match-breakpoints'
import { bugsnagNotify, bugsnagClient, getLocale as getLocaleUtil, setLocale, ucFirst, bugsnagSetUser, isJwtTokenExpired } from '@src/utils'
import { LoaderCircle, TriangleAlert, Info, Link2Off } from "lucide-react"
import storesApi from '@src/pages/stores/storesApi'
import LangFlags from '@components/LangFlags';
import { useBus, busDispatch } from '@hooks'
import { useDebouncedCallback } from 'use-debounce';
import { Button } from "@/components/ui/button"
import pRetry from 'p-retry'
import ms from 'ms'

import 'react-day-picker/style.css'
import 'react-international-phone/style.css'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import '@yaireo/tagify/dist/tagify.css'
import './main.css'

import('@translations').then(({ initTranslations }) => initTranslations())

const AllOrders = lazy(() => import('@pages/orders/Orders'))
const UserOrders = lazy(() => import('@pages/orders/UserOrders'))

const LoaderComponent = () => <div className="m-auto flex"><LoaderCircle className="m-auto animate-spin size-24 opacity-80 text-white" strokeWidth={1} /></div>

const Orders = () => {
  const { user: authUser } = useSelector(({ auth }) => auth)

  if (authUser.role === 'user') {
    return <UserOrders />
  }

  return <AllOrders />
}

const breakpointsConfig = {
  xxs: 'screen and (min-width: 320px)',
  xs: 'screen and (min-width: 480px)',
  sm: 'screen and (min-width: 640px)',
  md: 'screen and (min-width: 768px)',
  lg: 'screen and (min-width: 1024px)',
  xl: 'screen and (min-width: 1280px)',
  xl2: 'screen and (min-width: 1536px)',
}

const BreakpointsProvider = initBreakpoints(breakpointsConfig)
const ErrorBoundary = bugsnagClient.getPlugin('react').createErrorBoundary(React)

const FallbackComponent = ({ data }) => {
  const locale = getLocale()
  const error = useRouteError() || {};
  const isDev = bugsnagClient._config.releaseStage === 'development'

  if (!error && !data) {
    return (
      <div className="flex min-h-screen justify-center items-center w-full">
        <div className="flex bg-white justify-center items-center px-6 py-2 rounded-lg cursor-pointer" onClick={() => window.location.replace('/')}>
          <TriangleAlert className="w-10 h-10 text-red-700" />
          <div className="mx-4 text-md">
            <p className="">{t('error_loading')}</p>
            <p className="font-bold">{t('please_reload')}</p>
          </div>
        </div>
      </div>
    )
  }

  if (data && isDev) {
    return (
      <div className="flex min-h-screen bg-black w-full">
        <div className="m-auto">
          <pre className="my-2 font-bold text-red-500">{data.error.toString()}</pre>
          <pre className="w-full text-white">{data.info.componentStack}</pre>
        </div>
      </div>
    )
  }

  if (error.status !== 404) {
    return (
      <div className="flex min-h-screen justify-center items-center w-full px-4">
        <div className="flex bg-white justify-center items-center px-6 py-2 rounded-lg cursor-pointer" onClick={() => window.location.replace('/')}>
          <TriangleAlert className="w-10 h-10 text-red-700" />
          <div className="mx-4 text-md">
            <p className="">{isDev ? (error.statusText || error.message || '?') : t('error_loading')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen justify-center items-center w-full">
      <div className="flex bg-white justify-center items-center px-6 py-2 rounded-lg cursor-pointer" onClick={() => window.location.replace('/')}>
      <Link2Off className="w-10 h-10 text-blue-500" />
      <div className="mx-4 text-md">
        <p className="">{error.statusText || error.message || t('error_loading')}</p>
      </div>
      </div>
    </div>
  )
}

const container = document.getElementById('root');
const root = createRoot(container)

const router = createBrowserRouter([
  {
    path: "/",
    Component: lazy(() => import('@pages/layout')),
    children: [
      {
        path: "",
        Component: lazy(() => import('@pages/home/Home')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "albums/:id",
        Component: lazy(() => import('@pages/home/Home')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "login",
        Component: lazy(() => import('@pages/login/Login')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "media",
        Component: lazy(() => import('@pages/media/Media')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "help",
        Component: lazy(() => import('@pages/help/Help')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "signup",
        Component: lazy(() => import('@pages/signup/SignUp')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "sync",
        Component: lazy(() => import('@pages/sync/Sync')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "profile",
        Component: lazy(() => import('@pages/profile/Profile')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "cart",
        Component: lazy(() => import('@pages/cart/Cart')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "store",
        Component: lazy(() => import('@pages/store/Store')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: true }))

          return { storeData: data }
        },
      },
      {
        path: "logs",
        Component: lazy(() => import('@pages/logs/Logs')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: true }))

          return { storeData: data }
        },
      },
      {
        path: "dashboard",
        Component: lazy(() => import('@pages/dashboard/Dashboard')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "addons",
        Component: lazy(() => import('@pages/addons/AddOns')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "carts",
        Component: lazy(() => import('@pages/carts/Carts')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "coupons",
        Component: lazy(() => import('@pages/coupons/Coupons')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "users",
        Component: lazy(() => import('@pages/users/Users')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "orders",
        Component: Orders,
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "stats",
        Component: lazy(() => import('@pages/stats/Stats')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "albums",
        Component: lazy(() => import('@pages/albums/Albums')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "upload",
        Component: lazy(() => import('@pages/upload/Upload')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
      {
        path: "costs",
        Component: lazy(() => import('@pages/costs/Costs')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

          return { storeData: data }
        },
      },
    ],
    HydrateFallback: LoaderComponent,
    loader: async ({ request, params }) => {
      const fetcher = async () => {
        try {
          return await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false })).unwrap()
        } catch (e) {
          bugsnagNotify(e, event => { event.addMetadata('metadata', { request, params }) })
          throw new Error('Error fetching Store data!')
        }
      }

      return { storeData: await pRetry(fetcher, { retries: 2 }) }
    },
    errorElement: <I18n render={(...props) => <FallbackComponent {...props} />} />
  },
  {
    path: "tv",
    Component: lazy(() => import('@pages/tv/Tv')),
    HydrateFallback: LoaderComponent,
    loader: async ({ request, params }) => {
      const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(null, { forceRefetch: false }))

      return { storeData: data }
    },
  },
]);

root.render(
  <React.StrictMode>
    <ErrorBoundary FallbackComponent={(data) => <I18n render={() => <FallbackComponent data={data} />} />}>
      <Provider store={store}>
        <BreakpointsProvider>
            <div data-slot="sidebar-wrapper"className="group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex flex-wrap min-h-lvh w-full">
              <RouterProvider router={router} />
            </div>
        </BreakpointsProvider>
      </Provider>
    </ErrorBoundary>
  </React.StrictMode>
)
